import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CATEGORY_MAP, defaultTagColor } from '@tally/shared';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { insightsApi, type InsightsData, type MerchantTotal, type PeriodTotal } from '@/services/api';
import { cn } from '@/utils/cn';
import { formatUsd } from '@/utils/money';
import { monthLabel } from '@/utils/time';
import { AXIS_TICK, CHART_TOOLTIP, SERIES_COLORS, compactUsd } from '@/components/charts/chartTheme';
import { MerchantPanel } from '@/components/insights/MerchantPanel';

type RangeKey = PeriodTotal['key'] | 'custom';
type SortKey = 'totalUsd' | 'count' | 'avgUsd' | 'last' | 'name';

const RANGE_LABEL: Record<PeriodTotal['key'], string> = { '3m': '3M', '1y': '1Y', '2y': '2Y', '3y': '3Y', all: 'All' };
const PAGE = 15;

export function InsightsPage() {
  const [range, setRange] = useState<RangeKey>('1y');
  const [custom, setCustom] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [merchant, setMerchant] = useState<{ key: string; name: string } | null>(null);

  // All-time first: it carries the rolling-period totals and their start dates.
  const allTime = useQuery({ queryKey: ['insights', 'all'], queryFn: () => insightsApi.get({}) });
  const periods = allTime.data?.data?.periods ?? [];
  const rangeParams = range === 'custom'
    ? { from: custom.from || undefined, to: custom.to || undefined }
    : { from: periods.find((p) => p.key === range)?.from ?? undefined };
  const needsOwnQuery = range !== 'all' && (range !== 'custom' || !!custom.from || !!custom.to);
  const ranged = useQuery({
    queryKey: ['insights', rangeParams],
    queryFn: () => insightsApi.get(rangeParams),
    enabled: needsOwnQuery && (range === 'custom' || periods.length > 0),
  });
  const data: InsightsData | undefined = needsOwnQuery ? ranged.data?.data : allTime.data?.data;
  const loading = allTime.isLoading || (needsOwnQuery && ranged.isLoading);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Insights</h1>
        <p className="text-sm text-muted-foreground">How much, where and when: spending over any period, per merchant, category and tag</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {(periods.length ? periods : (['3m', '1y', '2y', '3y', 'all'] as const).map((key) => ({ key, label: '', totalUsd: 0, count: 0, from: null }))).map((p) => (
          <button
            key={p.key}
            onClick={() => setRange(p.key)}
            className={cn(
              'rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary/50',
              range === p.key ? 'border-primary ring-1 ring-primary' : 'border-border',
              p.key === 'all' && 'col-span-2 sm:col-span-1'
            )}
          >
            <p className="text-xs text-muted-foreground">{p.label || '…'}</p>
            {allTime.isLoading ? <div className="mt-1 h-7 w-24 animate-pulse rounded bg-muted" /> : <p className="mt-1 text-xl font-bold">{formatUsd(p.totalUsd)}</p>}
            <p className="text-xs text-muted-foreground">{p.count} expense{p.count === 1 ? '' : 's'}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-border p-1" role="tablist" aria-label="Range">
          {(Object.keys(RANGE_LABEL) as Array<PeriodTotal['key']>).map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={range === k}
              onClick={() => setRange(k)}
              className={cn('rounded-md px-3 py-1.5 text-sm font-medium', range === k ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')}
            >
              {RANGE_LABEL[k]}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            aria-label="From"
            value={custom.from}
            onChange={(e) => { setCustom({ ...custom, from: e.target.value }); setRange('custom'); }}
            className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <input
            type="date"
            aria-label="To"
            value={custom.to}
            onChange={(e) => { setCustom({ ...custom, to: e.target.value }); setRange('custom'); }}
            className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm"
          />
        </div>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">{[...Array(4)].map((_, i) => <div key={i} className="h-72 animate-pulse rounded-xl bg-muted" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label="Spent" value={formatUsd(data.totalUsd)} hint={rangeHint(data)} />
            <Kpi label="Per month" value={formatUsd(data.avgPerMonthUsd)} hint={`average over ${data.months} month${data.months === 1 ? '' : 's'}`} />
            <Kpi label="Expenses" value={String(data.count)} hint={`${data.merchants.length} merchants`} />
            <Kpi label="Top merchant" value={data.merchants[0]?.name ?? '—'} hint={data.merchants[0] ? formatUsd(data.merchants[0].totalUsd) : ''} />
          </div>

          <SpendingOverTime data={data} />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <TopMerchants merchants={data.merchants} onPick={(m) => setMerchant({ key: m.key, name: m.name })} />
            <Breakdown data={data} />
          </div>

          <MerchantTable merchants={data.merchants} onPick={(m) => setMerchant({ key: m.key, name: m.name })} />
        </>
      )}

      {merchant && <MerchantPanel merchantKey={merchant.key} name={merchant.name} onClose={() => setMerchant(null)} />}
    </div>
  );
}

function rangeHint(d: InsightsData): string {
  if (!d.from && !d.to) return 'all time';
  return `${d.from ? monthLabel(d.from) : 'start'} – ${d.to ? monthLabel(d.to) : 'now'}`;
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-xl font-bold sm:text-2xl" title={value}>{value}</p>
      <p className="truncate text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Chart 1: spending per month, trip vs everyday, with the average as a line. */
function SpendingOverTime({ data }: { data: InsightsData }) {
  const series = data.byMonth.map((m) => ({ ...m, label: monthLabel(m.month) }));
  return (
    <Card title="Spending over time">
      {series.length === 0 ? <Empty /> : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ left: 0, right: 8 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={AXIS_TICK} minTickGap={24} />
              <YAxis tick={AXIS_TICK} tickFormatter={compactUsd} width={52} />
              <Tooltip {...CHART_TOOLTIP} formatter={(v: number, name: string) => [formatUsd(v), name]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={data.avgPerMonthUsd} stroke="#f59e0b" strokeDasharray="4 4" />
              <Bar dataKey="otherUsd" name="Everyday" stackId="s" fill="#6366f1" />
              <Bar dataKey="tripUsd" name="On trips" stackId="s" fill="#14b8a6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

/** Chart 2: the ten merchants that took the most; a bar opens the merchant. */
function TopMerchants({ merchants, onPick }: { merchants: MerchantTotal[]; onPick: (m: MerchantTotal) => void }) {
  const top = merchants.filter((m) => m.totalUsd > 0).slice(0, 10);
  return (
    <Card title="Top merchants">
      {top.length === 0 ? <Empty /> : (
        <div style={{ height: Math.max(200, top.length * 34) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top} layout="vertical" margin={{ left: 0, right: 12 }}>
              <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tick={AXIS_TICK} tickFormatter={compactUsd} />
              <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={110} />
              <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [formatUsd(v), 'Spent']} />
              <Bar dataKey="totalUsd" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d: MerchantTotal) => onPick(d)}>
                {top.map((_, i) => <Cell key={i} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

/** Chart 3: where the money went, by category or by tag. */
function Breakdown({ data }: { data: InsightsData }) {
  const [by, setBy] = useState<'category' | 'tag'>('category');
  const slices = useMemo(() => {
    const raw = by === 'category'
      ? data.categories.map((c) => ({ name: CATEGORY_MAP.get(c.category)?.label ?? c.category, value: c.totalUsd, color: CATEGORY_MAP.get(c.category)?.color ?? '#94a3b8' }))
      : data.tags.map((t) => ({ name: t.name, value: t.totalUsd, color: t.color ?? defaultTagColor(t.name) }));
    const positive = raw.filter((s) => s.value > 0);
    const top = positive.slice(0, 7);
    const rest = positive.slice(7).reduce((s, x) => s + x.value, 0);
    return rest > 0 ? [...top, { name: 'Other', value: Math.round(rest * 100) / 100, color: '#94a3b8' }] : top;
  }, [by, data]);
  const total = slices.reduce((s, x) => s + x.value, 0);

  return (
    <Card
      title="Where it goes"
      action={
        <div className="flex items-center gap-1 rounded-lg border border-border p-0.5 text-xs" role="tablist" aria-label="Group by">
          {(['category', 'tag'] as const).map((k) => (
            <button key={k} role="tab" aria-selected={by === k} onClick={() => setBy(k)} className={cn('rounded-md px-2.5 py-1 font-medium capitalize', by === k ? 'bg-accent' : 'text-muted-foreground')}>
              {k === 'category' ? 'Category' : 'Tag'}
            </button>
          ))}
        </div>
      }
    >
      {slices.length === 0 ? <Empty text={by === 'tag' ? 'No tagged spending in this range yet.' : undefined} /> : (
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="h-56 w-full sm:w-1/2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="90%" paddingAngle={2} stroke="none">
                  {slices.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip {...CHART_TOOLTIP} formatter={(v: number, name: string) => [formatUsd(v), name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="w-full space-y-1.5 text-sm sm:w-1/2">
            {slices.map((s) => (
              <li key={s.name} className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="flex-1 truncate">{s.name}</span>
                <span className="text-xs text-muted-foreground">{total ? Math.round((s.value / total) * 100) : 0}%</span>
                <span className="w-20 text-right font-medium">{formatUsd(s.value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** Every merchant with totals for the range: search, sort, open one. */
function MerchantTable({ merchants, onPick }: { merchants: MerchantTotal[]; onPick: (m: MerchantTotal) => void }) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'totalUsd', desc: true });
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? merchants.filter((m) => m.name.toLowerCase().includes(q)) : merchants;
    const dir = sort.desc ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      return (typeof x === 'string' ? x.localeCompare(String(y)) : (x as number) - (y as number)) * dir;
    });
  }, [merchants, query, sort]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice((page - 1) * PAGE, page * PAGE);

  const header = (key: SortKey, label: string, right = false) => (
    <th className={cn('px-4 py-3 font-medium', right && 'text-right')}>
      <button
        onClick={() => { setSort({ key, desc: sort.key === key ? !sort.desc : key !== 'name' }); setPage(1); }}
        className={cn('inline-flex items-center gap-1 hover:text-foreground', right && 'flex-row-reverse')}
      >
        {label}
        {sort.key !== key ? <ArrowUpDown className="h-3 w-3 opacity-40" /> : sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
      </button>
    </th>
  );

  return (
    <Card
      title="Merchants"
      action={
        <div className="relative w-44 sm:w-64">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Find a merchant…"
            aria-label="Find a merchant"
            className="w-full rounded-lg border border-input bg-background py-1.5 pl-8 pr-3 text-sm"
          />
        </div>
      }
    >
      {/* Table on wider screens, cards on phones. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left text-xs text-muted-foreground">
            <tr>
              {header('name', 'Merchant')}
              {header('count', 'Charges', true)}
              {header('avgUsd', 'Average', true)}
              {header('totalUsd', 'Total', true)}
              {header('last', 'Last charge', true)}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {shown.map((m) => (
              <tr key={m.key} onClick={() => onPick(m)} className="cursor-pointer hover:bg-accent/30">
                <td className="px-4 py-2.5 font-medium">{m.name}</td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">{m.count}</td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">{formatUsd(m.avgUsd)}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{formatUsd(m.totalUsd)}</td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">{m.last}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="divide-y divide-border md:hidden">
        {shown.map((m) => (
          <li key={m.key}>
            <button onClick={() => onPick(m)} className="flex w-full items-center justify-between gap-3 py-3 text-left">
              <span className="min-w-0">
                <span className="block truncate font-medium">{m.name}</span>
                <span className="text-xs text-muted-foreground">{m.count} charge{m.count === 1 ? '' : 's'} · last {m.last}</span>
              </span>
              <span className="shrink-0 font-semibold">{formatUsd(m.totalUsd)}</span>
            </button>
          </li>
        ))}
      </ul>
      {rows.length === 0 && <Empty text="No merchants match." />}
      {pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg p-1.5 hover:bg-accent disabled:opacity-30" aria-label="Previous page">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span>Page {page} of {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="rounded-lg p-1.5 hover:bg-accent disabled:opacity-30" aria-label="Next page">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </Card>
  );
}

function Empty({ text = 'Nothing spent in this range.' }: { text?: string }) {
  return <p className="py-10 text-center text-sm text-muted-foreground">{text}</p>;
}
